import {EditorSelection, type Extension} from '@codemirror/state'
import {type EditorView, layer, RectangleMarker} from '@codemirror/view'

import {getLineRanges} from '../../language-tools/source-utils'
import {getAccurateCaretCoords} from '../utils'
import {hasBeenFocused, isFocused} from './focused'

function shouldDrawBlurredSelection(view: EditorView) {
  return hasBeenFocused(view.state) && !isFocused(view.state)
}

/*
 * Based on https://github.com/codemirror/view/blob/main/src/layer.ts
 */

// Helper function to get base coordinates
function getBase(view: EditorView) {
  const rect = view.scrollDOM.getBoundingClientRect()
  const top = Math.max(0, rect.top)
  return {left: rect.left, top}
}

/*
 * Based on https://github.com/codemirror/view/blob/main/src/draw-selection.ts
 */

export function drawSelection(): Extension {
  return [cursorLayer, selectionLayer]
}

const cursorLayer = layer({
  above: true,
  markers(view) {
    if (!shouldDrawBlurredSelection(view)) return []

    const {state} = view
    const cursors = []
    for (const r of state.selection.ranges) {
      const prim = r === state.selection.main
      const className = prim ? 'cm-cursor cm-cursor-primary' : 'cm-cursor cm-cursor-secondary'
      const cursor = r.empty ? r : EditorSelection.cursor(r.head, r.head > r.anchor ? -1 : 1)
      for (const piece of RectangleMarker.forRange(view, className, cursor)) cursors.push(piece)
    }
    return cursors
  },
  update() {
    return true
  },
  class: 'cm-cursorLayer',
})

const selectionLayer = layer({
  above: false,
  markers(view) {
    if (!shouldDrawBlurredSelection(view)) return []

    const source = view.state.doc.toString()
    return view.state.selection.ranges
      .map(r => {
        if (r.empty) return []

        // Create custom selection background markers based on line ranges to highlight only the selection
        // https://discuss.codemirror.net/t/drawselection-highlights-whole-line-on-line-breaks/9495
        const lineRanges = getLineRanges(source, r.from, r.to)
        const className = 'cm-selectionBackground'
        return lineRanges.flatMap(range => {
          const browserRange = EditorSelection.range(Math.max(range.start, r.from), Math.min(range.end, r.to))

          // Handle background markers for empty selections
          // TODO: Some browsers show a thin line, others show nothing
          if (browserRange.empty) {
            const pos = getAccurateCaretCoords(view, browserRange.head)
            if (!pos) return []
            const base = getBase(view)
            // 3px is an arbitrary width for empty selections
            return [new RectangleMarker(className, pos.left - base.left, pos.top - base.top, 3, pos.bottom - pos.top)]
          } else {
            return RectangleMarker.forRange(view, className, browserRange)
          }
        })
      })
      .reduce((a, b) => a.concat(b))
  },
  update() {
    return true
  },
  class: 'cm-selectionLayer',
})
